/**
 * Client mirror of the Digistore24 gate + value extraction in
 * apps/api/src/lib/digistore.ts — powers the sample preview in
 * DigistorePanel (WebhookEndpointDetail.tsx). Keep in sync. This only
 * extracts the subset of fields the preview actually renders (deal_value,
 * deal_cash_in, currency, order_id, email, voucher) — not the full
 * MappedEvent (keys/fields/custom_data) the server produces.
 */

function str(p: Record<string, unknown>, key: string): string | undefined {
  const v = p[key];
  if (typeof v === "string") return v.trim() || undefined;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return undefined;
}

function num(p: Record<string, unknown>, key: string): number | undefined {
  const s = str(p, key);
  if (s === undefined) return undefined;
  const n = Number(s.replace(",", "."));
  return Number.isFinite(n) ? n : undefined;
}

function unwrap(payload: unknown): unknown {
  return Array.isArray(payload) && payload.length > 0 ? payload[0] : payload;
}

/**
 * TASK-27: same zapier-shape signal as apps/api/src/lib/digistore.ts —
 * Zapier-routed orders carry a `transaction: { type: "payment", … }` object
 * instead of `event`/`pay_sequence_no`, and no `ipn_version`.
 */
function zapierTransaction(
  p: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const tx = p["transaction"];
  if (!tx || typeof tx !== "object" || Array.isArray(tx)) return undefined;
  const rec = tx as Record<string, unknown>;
  return typeof rec["type"] === "string" ? rec : undefined;
}

/**
 * TASK-199: mirrors digistoreRefundType in apps/api/src/lib/digistore.ts —
 * keep in sync.
 */
function digistoreRefundType(payload: unknown): "refund" | "chargeback" | undefined {
  const p = (unwrap(payload) ?? {}) as Record<string, unknown>;
  const tx = zapierTransaction(p);
  const name = (tx ? str(tx, "type") : (str(p, "event") ?? str(p, "function_call")))
    ?.toLowerCase();
  if (!name) return undefined;
  if (name.includes("chargeback")) return "chargeback";
  if (name.includes("refund")) return "refund";
  return undefined;
}

export function digistoreShouldTrack(payload: unknown): {
  track: boolean;
  reason: string;
} {
  const refundType = digistoreRefundType(payload);
  if (refundType) {
    return { track: true, reason: `Rückgabe (${refundType}) — wird als Refund erfasst` };
  }
  const p = (unwrap(payload) ?? {}) as Record<string, unknown>;
  const tx = zapierTransaction(p);
  if (tx) {
    const type = str(tx, "type") ?? "";
    return type === "payment"
      ? { track: true, reason: 'transaction.type="payment"' }
      : {
          track: false,
          reason: `transaction.type="${type}" — nur "payment" wird getrackt`,
        };
  }
  const event = str(p, "event") ?? str(p, "function_call");
  if (event && event !== "on_payment") {
    return { track: false, reason: `Event "${event}" — nur on_payment wird getrackt` };
  }
  const seq = num(p, "pay_sequence_no");
  if (seq !== undefined && seq > 1) {
    return { track: false, reason: `Folgezahlung (pay_sequence_no=${seq})` };
  }
  if (seq === undefined) {
    return { track: true, reason: "keine pay_sequence_no — Einmalzahlung" };
  }
  return {
    track: true,
    reason:
      seq === 0
        ? "Einmalzahlung (pay_sequence_no=0)"
        : "Erstzahlung (pay_sequence_no=1)",
  };
}

/**
 * TASK-85: mirrors isDigistoreTestPurchase in apps/api/src/lib/digistore.ts
 * — keep in sync. Powers the "Testkauf" badge per sample row in
 * WebhookEndpointDetail.tsx; the actual receive-path gating lives server-
 * side (routes/wh.ts), this is display-only.
 */
export function isDigistoreTestPurchase(payload: unknown): {
  isTest: boolean;
  reason: string;
} {
  const p = (unwrap(payload) ?? {}) as Record<string, unknown>;
  const tx = zapierTransaction(p);
  if (tx) {
    const payMethod = str(tx, "pay_method")?.toLowerCase();
    return payMethod === "test"
      ? { isTest: true, reason: 'transaction.pay_method="test"' }
      : { isTest: false, reason: "" };
  }
  const apiMode = str(p, "api_mode")?.toLowerCase();
  const payMethod = str(p, "pay_method")?.toLowerCase();
  // api_mode="test" alone is authoritative (Digistore's own test-mode flag —
  // no real revenue can carry it); pay_method="test" is kept as an
  // additional OR signal. Keep in sync with isDigistoreTestPurchase in
  // apps/api/src/lib/digistore.ts.
  if (apiMode === "test") return { isTest: true, reason: 'api_mode="test"' };
  if (payMethod === "test") return { isTest: true, reason: 'pay_method="test"' };
  return { isTest: false, reason: "" };
}

export function digistoreValues(payload: unknown): {
  deal_value?: string;
  deal_cash_in?: string;
  currency?: string;
  order_id?: string;
  email?: string;
  voucher?: string;
} {
  const p = (unwrap(payload) ?? {}) as Record<string, unknown>;
  const tx = zapierTransaction(p);
  if (tx) return zapierValues(p, tx);
  const isRefund = digistoreRefundType(p) !== undefined;

  let total = 0;
  let hasProductAmount = false;
  for (const key of Object.keys(p)) {
    if (key === "product_netto_amount" || /^product_netto_amount_\d+$/.test(key)) {
      hasProductAmount = true;
      total += num(p, key) ?? 0;
    }
  }
  const amountNetto = num(p, "amount_netto");
  const voucher = str(p, "voucher_code");
  const installments = num(p, "number_of_installments") ?? 0;
  if (voucher && amountNetto !== undefined) {
    total = installments > 0 ? amountNetto * installments : amountNetto;
  } else if (!hasProductAmount && amountNetto !== undefined) {
    total = amountNetto;
  }
  const out: ReturnType<typeof digistoreValues> = {};
  // TASK-201: a refund shows the whole contract negated plus the refunded money.
  const signed = (n: number) => (isRefund ? -Math.abs(n) : n);
  if (hasProductAmount || amountNetto !== undefined) {
    out.deal_value = String(Math.round(signed(total) * 100) / 100);
  }
  if (amountNetto !== undefined) {
    out.deal_cash_in = String(Math.round(signed(amountNetto) * 100) / 100);
  }
  const currency = str(p, "currency") ?? str(p, "transaction_currency");
  if (currency) out.currency = currency;
  const orderId = str(p, "order_id");
  if (orderId) out.order_id = orderId;
  const email = str(p, "email") ?? str(p, "buyer_email");
  if (email) out.email = email;
  if (voucher) out.voucher = voucher;
  return out;
}

/**
 * TASK-27: Zapier-routed samples carry nested address/item_N/amount_details
 * objects instead of flat buyer_* / product_netto_amount* fields — mirrors
 * mapDigistoreZapier in apps/api/src/lib/digistore.ts, restricted to the
 * fields the preview renders (no voucher concept in this shape).
 */
function zapierValues(
  p: Record<string, unknown>,
  tx: Record<string, unknown>,
): ReturnType<typeof digistoreValues> {
  const out: ReturnType<typeof digistoreValues> = {};
  const addressRaw = p["address"];
  const address =
    addressRaw && typeof addressRaw === "object" && !Array.isArray(addressRaw)
      ? (addressRaw as Record<string, unknown>)
      : {};

  // Order value: sum across item_1…item_N slots. Empty slots carry `false`
  // placeholders; num() already turns those into undefined (Number("false")
  // is NaN), so they're skipped without special-casing `false`.
  //
  // WHICH amount depends on the billing type — same rule as the server
  // (mapDigistoreZapier, TASK-95/96). product_netto_amount is the
  // undiscounted list price, product_txn_netto_amount what is charged.
  // Only an explicit single_payment switches; instalment/subscription keep
  // the list price, which is the full contract value. Diverging from the
  // server here would show two different figures for one order.
  const isOneOff = str(p, "billing_type") === "single_payment";
  let total = 0;
  let hasProductAmount = false;
  for (const key of Object.keys(p)) {
    if (!/^item_\d+$/.test(key)) continue;
    const item = p[key];
    if (!item || typeof item !== "object" || Array.isArray(item)) continue;
    const slot = item as Record<string, unknown>;
    const amt =
      (isOneOff ? num(slot, "product_txn_netto_amount") : undefined) ??
      num(slot, "product_netto_amount");
    if (amt !== undefined) {
      hasProductAmount = true;
      total += amt;
    }
  }
  // TASK-201: refund = whole contract negated (fallback: refunded amount),
  // deal_cash_in = minus the refunded money — mirrors mapDigistoreZapier.
  const isRefund = digistoreRefundType(p) !== undefined;
  const refunded = num(tx, "amount");
  const round = (n: number) => String(Math.round(n * 100) / 100);
  if (hasProductAmount) {
    out.deal_value = round(isRefund ? -Math.abs(total) : total);
  } else if (isRefund && refunded !== undefined) {
    out.deal_value = round(-Math.abs(refunded));
  }

  if (isRefund) {
    if (refunded !== undefined) out.deal_cash_in = round(-Math.abs(refunded));
  } else {
    const amountDetailsRaw = p["amount_details"];
    if (
      amountDetailsRaw &&
      typeof amountDetailsRaw === "object" &&
      !Array.isArray(amountDetailsRaw)
    ) {
      const netto = num(amountDetailsRaw as Record<string, unknown>, "netto");
      if (netto !== undefined) out.deal_cash_in = round(netto);
    }
  }

  const currency = str(tx, "currency");
  if (currency) out.currency = currency;
  const orderId = str(p, "order_id");
  if (orderId) out.order_id = orderId;
  const email = str(address, "email");
  if (email) out.email = email;
  return out;
}
